/**
 * Demo Mode
 *
 * Controls whether the app uses mock/seed data (demo mode) or the real Go API
 * (production mode). The initial value is set from the VITE_DEMO_MODE env var
 * at build time, but can be toggled at runtime and persisted to localStorage.
 *
 * When demo mode is toggled, ALL TanStack Query caches are cleared so every
 * query re-runs immediately with the new mode.
 *
 * Usage:
 *   import { useDemoMode, isDemoMode } from "@/lib/demo-mode";
 *
 *   // In a React component:
 *   const { demoMode, toggleDemoMode } = useDemoMode();
 *
 *   // Outside React (e.g. in API interceptors, main.tsx):
 *   if (isDemoMode()) { ... }
 */

import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { env } from "./env";

const STORAGE_KEY = "skul-demo-mode";

interface DemoModeState {
  demoMode: boolean;
  setDemoMode: (enabled: boolean) => void;
  toggleDemoMode: () => void;
}

export const useDemoModeStore = create<DemoModeState>()(
  devtools(
    persist(
      subscribeWithSelector((set, get) => ({
        // Seed initial value from build-time env var, but allow runtime override.
        demoMode: env.VITE_DEMO_MODE,
        setDemoMode: (enabled) => set({ demoMode: enabled }),
        toggleDemoMode: () => set({ demoMode: !get().demoMode }),
      })),
      { name: STORAGE_KEY },
    ),
    { name: "demo-mode" },
  ),
);

/** React hook — use inside components */
export const useDemoMode = useDemoModeStore;

/**
 * Wire up automatic query-cache clearing when demo mode is toggled.
 * Call once after the QueryClient is available (in main.tsx or App.tsx).
 */
export function subscribeToQueryCacheClear(clearAll: () => void): () => void {
  return useDemoModeStore.subscribe(
    (state) => state.demoMode,
    () => {
      clearAll();
    },
  );
}

/**
 * Synchronous read outside React (interceptors, main.tsx).
 * Reads from localStorage directly so it works before the store initialises.
 */
export function isDemoMode(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: { demoMode?: boolean } };
      if (typeof parsed?.state?.demoMode === "boolean") {
        return parsed.state.demoMode;
      }
    }
  } catch {
    // ignore
  }
  // Fall back to build-time env
  return env.VITE_DEMO_MODE;
}
